import { Serwist } from '@serwist/window';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('ServiceWorker', LogLevel.INFO);

export const SW_UPDATE_AVAILABLE_EVENT = 'sw-update-available';

let serwist: Serwist | null = null;

/**
 * Register the service worker and wire up the update lifecycle.
 *
 * The SW is registered without skipWaiting/clientsClaim, so a new build
 * stays in the "waiting" state until the user explicitly applies it via
 * applyServiceWorkerUpdate(). This prevents the page from reloading
 * mid-action (e.g., during passkey unlock) after a deploy.
 */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_SW_IN_DEV !== 'true') {
    return;
  }

  serwist = new Serwist('/sw.js', { scope: '/' });

  serwist.addEventListener('installed', () => {
    logger.info('Service worker installed');
  });

  serwist.addEventListener('waiting', () => {
    logger.info('Service worker waiting — update available');
    window.dispatchEvent(new CustomEvent(SW_UPDATE_AVAILABLE_EVENT));
  });

  serwist.addEventListener('controlling', () => {
    logger.info('Service worker controlling — reloading');
    window.location.reload();
  });

  // Registration can fail for reasons outside the app: Chromium aborts it
  // when the page unloads mid-flight, and Playwright contexts that block
  // service workers make register() resolve undefined (Serwist then throws
  // reading `.waiting`). The app works without a SW until the next load, so
  // this is a warning, not an unhandled rejection.
  serwist.register().catch((error: unknown) => {
    logger.warn('Service worker registration failed', error);
  });

  // Re-check for updates when the user refocuses the tab. Without this,
  // long-lived sessions can run a stale bundle for hours after a deploy.
  // serwist is guaranteed non-null here — the listener is registered after
  // the assignment above and we only register it once per page load.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      serwist!.update().catch(() => {
        // Update check failures are expected when offline.
      });
    }
  });
}

export type AppUpdateCheckResult =
  /** A newer build has been downloaded and is waiting; applyServiceWorkerUpdate() installs it. */
  | 'update-available'
  /** The server has no newer build than the one running. */
  | 'up-to-date'
  /** No service worker in this environment (dev without VITE_ENABLE_SW_IN_DEV, unsupported browser). */
  | 'unavailable'
  /** The server could not be reached to check. */
  | 'failed';

/**
 * Ask the browser to look for a newer build right now, instead of waiting
 * for the next tab refocus. Used by the "Check for app updates" control in
 * Settings > Sync when a user reports not seeing a change.
 */
export async function checkForAppUpdate(): Promise<AppUpdateCheckResult> {
  if (!serwist || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return 'unavailable';
  }
  try {
    await serwist.update();
  } catch (error) {
    logger.warn('Update check failed', error);
    return 'failed';
  }
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (registration?.waiting) return 'update-available';
  if (registration?.installing) {
    // A new build is still downloading; give it a moment to reach "waiting".
    await new Promise((resolve) => setTimeout(resolve, 3000));
    const again = await navigator.serviceWorker.getRegistration('/');
    if (again?.waiting) return 'update-available';
  }
  return 'up-to-date';
}

/**
 * Tell the waiting service worker to activate. Once it does, the
 * `controlling` event fires and the page reloads.
 */
export function applyServiceWorkerUpdate(): void {
  if (!serwist) {
    // Indicates a startup-order bug: registerServiceWorker() should run
    // from index.tsx before any UI can dispatch this. Without the warning
    // the UI just sits in "Reloading…" with no signal of what went wrong.
    logger.warn('applyServiceWorkerUpdate called before registerServiceWorker — ignoring');
    return;
  }
  serwist.messageSkipWaiting();
}
